import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";

import { ErrorBoundary } from "@/components/error-boundary";
import { GuideWidget } from "@/components/guide/guide-widget";
import { ImpersonationBanner } from "@/components/impersonation-banner";
import { ImpersonationFrame } from "@/components/impersonation-frame";
import { Sidebar } from "@/components/navigation/Sidebar";
import { useSidebarState } from "@/components/navigation/use-sidebar-state";
import { CommandPalette } from "@/components/shell/command-palette";
import { MobileNav } from "@/components/shell/mobile-nav";
import { TopBar } from "@/components/shell/topbar";

export function AppLayout() {
  const [navOpen, setNavOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const nav = useSidebarState();

  // App-wide Cmd/Ctrl-K opens the command palette; "/" opens it too when the
  // user isn't already typing into a field (mirrors the AwesomeBar shortcut).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const cmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
      const target = e.target as HTMLElement | null;
      const typing =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable === true;
      const slash = e.key === "/" && !typing;
      if (cmdK || slash) {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div className="flex min-h-screen bg-bg">
      <Sidebar compact={nav.compact} isOpen={nav.isOpen} toggle={nav.toggle} />
      <MobileNav
        open={navOpen}
        onClose={() => setNavOpen(false)}
        isOpen={nav.isOpen}
        toggle={nav.toggle}
      />
      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
      <div className="flex min-w-0 flex-1 flex-col">
        <ImpersonationBanner />
        <TopBar
          onMenuOpen={() => setNavOpen(true)}
          onOpenSearch={() => setSearchOpen(true)}
          railCompact={nav.compact}
          onToggleRail={nav.toggleCompact}
        />
        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <div className="print-wrap mx-auto w-full max-w-[1440px]">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>
      {/* Read-only in-app help assistant, docked bottom-right. Renders nothing
          when Guide isn't configured for this deployment. */}
      <GuideWidget />
      {/* Ambient viewport ring while impersonating (renders nothing otherwise). */}
      <ImpersonationFrame />
    </div>
  );
}
